/**
 * PromptDialog — a controlled HTML dialog that captures one text value.
 *
 * The shared replacement for `window.prompt` (which we never use — it's
 * unstyled, unbranded, and blocks the event loop). Mirrors `ConfirmDialog`'s
 * shape: mount once near the trigger, drive `open` from local state, and get
 * the trimmed value back through `onConfirm`. Supports a single-line input or
 * a `multiline` textarea (for reasons), an optional `danger` intent (void /
 * override flows), and server-side validation: throw from `onConfirm` to keep
 * the dialog open with the user's text intact.
 *
 * @see ./confirm-dialog.tsx — the yes/no sibling
 */
import * as React from "react";
import { TriangleAlert } from "lucide-react";
import { Button } from "./button.js";
import { Input } from "./input.js";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog.js";

export interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short, action-oriented title (e.g. "Re-order this style"). */
  title: React.ReactNode;
  /** Optional body — what happens / how the value is used. */
  description?: React.ReactNode;
  /** Field label above the input. */
  label?: string;
  placeholder?: string;
  /** Prefill — reset into the field every time the dialog opens. */
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `'danger'` paints the confirm button red (void / override). */
  intent?: "default" | "danger";
  /** Render a textarea instead of a single-line input (reasons). */
  multiline?: boolean;
  /** Require a non-empty value to enable confirm. Default true. */
  required?: boolean;
  /** External pending flag (e.g. a mutation's `isPending`). */
  pending?: boolean;
  /**
   * Called with the trimmed value on confirm. May return a promise — the
   * button shows "Working…" until it resolves and the dialog closes on
   * success. Throw (or reject) to keep the dialog open for retry.
   */
  onConfirm: (value: string) => void | Promise<void>;
}

export function PromptDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  defaultValue = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  intent = "default",
  multiline = false,
  required = true,
  pending: externalPending = false,
  onConfirm,
}: PromptDialogProps) {
  const [value, setValue] = React.useState(defaultValue);
  const [internalPending, setInternalPending] = React.useState(false);
  const isPending = externalPending || internalPending;

  // Reset to the prefill each time the dialog opens.
  React.useEffect(() => {
    if (open) setValue(defaultValue);
  }, [open, defaultValue]);

  const trimmed = value.trim();
  const canConfirm = !isPending && (!required || trimmed !== "");

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setInternalPending(true);
    try {
      await onConfirm(trimmed);
      onOpenChange(false);
    } catch {
      // Keep the dialog open; the caller owns the user-visible error.
    } finally {
      setInternalPending(false);
    }
  };

  const fieldClass =
    "w-full rounded-sm border border-line bg-paper px-3 text-sm text-ink outline-none transition-colors placeholder:text-muted focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-subtle";

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-start gap-3">
            {intent === "danger" ? (
              <span
                aria-hidden
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bad-50 text-bad"
              >
                <TriangleAlert size={16} strokeWidth={2.4} />
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <DialogTitle>{title}</DialogTitle>
              {description ? <DialogDescription className="mt-1.5">{description}</DialogDescription> : null}
            </div>
          </div>
        </DialogHeader>

        <div className="py-1">
          {label ? <span className="mb-1.5 block text-xs font-medium text-ink-2">{label}</span> : null}
          {multiline ? (
            <textarea
              autoFocus
              rows={3}
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void handleConfirm();
                }
              }}
              className={`${fieldClass} resize-y py-2 leading-relaxed`}
            />
          ) : (
            <Input
              autoFocus
              value={value}
              placeholder={placeholder}
              onChange={(e) => setValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleConfirm();
                }
              }}
              className="h-9 text-sm"
            />
          )}
        </div>

        <DialogFooter>
          <Button type="button" intent="ghost" disabled={isPending} onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            intent={intent === "danger" ? "danger" : "primary"}
            disabled={!canConfirm}
            onClick={() => void handleConfirm()}
          >
            {isPending ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
